import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";

// import { useNavigate, useLocation } from "react-router";
import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";

import { DEV_AUTH_ENABLED, getMockSessionData } from "~/constants/mockUserData";
import { clearAllCache } from "~/lib/cache/clearCache";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "~/lib/localStorage";
import { loginUser, logoutUser, refreshToken } from "~/lib/payload/users";
import { identifyUser, trackEvent } from "~/providers/AnalyticsProvider";
import type { UserBase, UserRole, UserWithRole } from "~/types";
import { trackLogin } from "~/util/tracking";

// Define interfaces for the state and actions
interface SessionState {
  isAuthenticated: boolean;
  user: UserWithRole | null;
  exp: number | null;
  isLoading: boolean;
  isInitialLoad: boolean;
  isRefreshing: boolean;
  signIn: (
    email: string,
    password: string,
    role: UserRole,
    token?: string | null | undefined
  ) => Promise<{ success: boolean; message?: string | null | undefined }>;
  signOut: () => Promise<void>;
  setUnauthorized: () => void;
}

type SessionAction =
  | {
      type:
        | "INITIAL_LOAD"
        | "INITIAL_LOAD_COMPLETE"
        | "SIGN_IN_START"
        | "SIGN_IN_CANCEL"
        | "SIGN_OUT_START"
        | "REFRESH_START"
        | "REFRESH_END";
    }
  | { type: "SIGN_IN"; payload: { user: UserWithRole; exp: number } }
  | { type: "SIGN_OUT" };

// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = createContext<SessionState | undefined>(
  undefined
);

const initialState: SessionState = DEV_AUTH_ENABLED
  ? {
      ...getMockSessionData(),
      signIn: async () => ({ success: true }),
      signOut: async () => {},
      setUnauthorized: () => {},
    }
  : {
      isAuthenticated: false,
      user: null,
      exp: null,
      isLoading: true,
      isInitialLoad: true,
      isRefreshing: false,
      signIn: async () => ({ success: false }),
      signOut: async () => {},
      setUnauthorized: () => {},
    };

function sessionReducer(
  state: SessionState,
  action: SessionAction
): SessionState {
  switch (action.type) {
    case "INITIAL_LOAD":
      return { ...state, isLoading: true, isInitialLoad: true };
    case "INITIAL_LOAD_COMPLETE":
      return { ...state, isLoading: false, isInitialLoad: false };
    case "SIGN_IN_START":
      return { ...state, isLoading: true };
    case "SIGN_IN_CANCEL":
      return { ...state, isLoading: false };
    case "SIGN_OUT_START":
      return { ...state, isLoading: true };
    case "REFRESH_START":
      return { ...state, isRefreshing: true };
    case "REFRESH_END":
      return { ...state, isRefreshing: false };
    case "SIGN_IN":
      return {
        ...state,
        isAuthenticated: true,
        user: {
          ...action.payload.user,
          internalRole:
            action.payload.user.internalRole || state.user?.internalRole,
        },
        exp: action.payload.exp,
        isLoading: false,
        isRefreshing: false,
      };
    case "SIGN_OUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        exp: null,
        isLoading: false,
        isRefreshing: false,
      };
    default:
      return state;
  }
}

interface SessionProviderProps {
  children: ReactNode;
}

function getContactIdFromPermissions(user: UserBase): string | undefined {
  const contactIdFromPermissions = user?.organizationPermissions?.docs?.find(
    (perm) =>
      typeof perm !== "string" &&
      perm?.contact &&
      typeof perm.contact !== "string" &&
      perm.contact.email &&
      perm.contact.email.toLowerCase() === user?.email?.toLowerCase()
  );

  const contactId =
    typeof contactIdFromPermissions !== "string" &&
    contactIdFromPermissions?.contact &&
    typeof contactIdFromPermissions.contact !== "string"
      ? contactIdFromPermissions.contact.id
      : undefined;

  return contactId;
}

function SessionProvider({ children }: SessionProviderProps) {
  const appListener = useRef<PluginListenerHandle | null>(null);
  const refreshInProgress = useRef(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, dispatch] = useReducer(sessionReducer, initialState);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      role: UserRole,
      token?: string | null | undefined
    ) => {
      dispatch({ type: "SIGN_IN_START" });

      try {
        const loginRes = await loginUser(email, password, token);

        if (loginRes.success && loginRes.data) {
          // Atomic localStorage updates
          const localStorageUpdates = [
            () => setLocalStorageItem("hasAccount", true),
            () => setLocalStorageItem("loggedin", true),
            () => setLocalStorageItem("role", role),
          ];

          localStorageUpdates.forEach((update) => update());

          const contactId = getContactIdFromPermissions(loginRes.data.user);

          dispatch({
            type: "SIGN_IN",
            payload: {
              ...loginRes.data,
              user: {
                ...loginRes.data.user,
                internalRole: role,
                subscription:
                  loginRes.data?.user?.organizations?.[0]?.subscription || null,
                contactId: contactId,
              },
            },
          });

          try {
            await identifyUser({
              id: loginRes.data.user.id,
              name: `${loginRes.data.user.firstName} ${loginRes.data.user.lastName}`,
              email: loginRes.data.user.email,
              userType: role,
            });

            // Google tag
            trackLogin(loginRes.data.user.id, role);

            // Sentry/PostHog
            trackEvent("USER_LOGGED_IN");
          } catch (analyticsError) {
            console.warn("Analytics tracking failed:", analyticsError);
          }

          return { success: true };
        }

        dispatch({ type: "SIGN_IN_CANCEL" });

        const message =
          loginRes &&
          "message" in loginRes &&
          typeof loginRes.message === "string"
            ? loginRes.message
            : "Authentication failed";

        return {
          success: false,
          message,
        };
      } catch (error) {
        dispatch({ type: "SIGN_IN_CANCEL" });
        console.error("Sign in error:", error);
        return {
          success: false,
          message: "Network error during authentication",
        };
      }
    },
    []
  );

  const signOut = useCallback(async () => {
    dispatch({ type: "SIGN_OUT_START" });

    try {
      // Clear refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      await logoutUser();

      // Clean up localStorage atomically
      removeLocalStorageItem("loggedin");

      clearAllCache();

      dispatch({ type: "SIGN_OUT" });
    } catch (error) {
      console.error("Sign out error:", error);
      // Even if logout fails, clear local state
      dispatch({ type: "SIGN_OUT" });
    }
  }, []);

  const setUnauthorized = useCallback(() => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    removeLocalStorageItem("loggedin");

    clearAllCache();
    dispatch({ type: "SIGN_OUT" });
  }, []);

  const handleRefresh = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (refreshInProgress.current) {
      return;
    }

    refreshInProgress.current = true;
    dispatch({ type: "REFRESH_START" });

    try {
      const role = getLocalStorageItem("role");
      const wasLoggedIn = getLocalStorageItem("loggedin");

      if (role && wasLoggedIn) {
        const res = await refreshToken();

        if (res.success && res.data) {
          const contactId = getContactIdFromPermissions(res.data.user);

          dispatch({
            type: "SIGN_IN",
            payload: {
              ...res.data,
              user: {
                ...res.data.user,
                internalRole: role,
                subscription:
                  res.data?.user?.organizations?.[0]?.subscription || null,
                contactId: contactId,
              },
            },
          });

          try {
            await identifyUser({
              id: res.data.user.id,
              name: `${res.data.user.firstName} ${res.data.user.lastName}`,
              email: res.data.user.email,
              userType: role,
            });
          } catch (analyticsError) {
            console.warn("Analytics refresh failed:", analyticsError);
          }
        } else {
          setUnauthorized();
        }
      } else if (wasLoggedIn) {
        await signOut();
      } else {
        setUnauthorized();
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      setUnauthorized();
    } finally {
      refreshInProgress.current = false;
      dispatch({ type: "REFRESH_END" });
    }
  }, [setUnauthorized, signOut]);

  // Check if the user is already authenticated on the first render.
  useEffect(() => {
    if (DEV_AUTH_ENABLED) {
      // Skip auth check in dev bypass mode
      return;
    }

    const checkAuth = async () => {
      dispatch({ type: "INITIAL_LOAD" });
      await handleRefresh();
      dispatch({ type: "INITIAL_LOAD_COMPLETE" });
    };

    checkAuth();
  }, [handleRefresh, signOut]);

  // Secure token refresh scheduler
  useEffect(() => {
    if (DEV_AUTH_ENABLED) {
      // Skip token refresh in dev bypass mode
      return;
    }

    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (state.isAuthenticated && state.exp && !state.isRefreshing) {
      // Calculate time until token expires with safety buffer
      const expiresIn = state.exp * 1000 - Date.now() - 30000; // 30s buffer

      // Only schedule if token has reasonable time left (min 1 minute, max 15 minutes)
      const refreshTime = Math.max(60000, Math.min(expiresIn, 15 * 60 * 1000));

      if (refreshTime > 0) {
        refreshTimeoutRef.current = setTimeout(async () => {
          if (state.isAuthenticated && !refreshInProgress.current) {
            await handleRefresh();
          }
        }, refreshTime);
      }
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [state.isAuthenticated, state.exp, state.isRefreshing, handleRefresh]);

  // Secure app state change handling
  useEffect(() => {
    if (DEV_AUTH_ENABLED) {
      // Skip app state listener in dev bypass mode
      return;
    }

    let currentAppListener = appListener.current;

    const addAppListener = async () => {
      try {
        appListener.current = await App.addListener(
          "appStateChange",
          async ({ isActive }) => {
            if (
              isActive &&
              state.isAuthenticated &&
              state.exp &&
              !refreshInProgress.current
            ) {
              const expiresIn = state.exp * 1000 - Date.now();
              // Refresh if token expires within 30 seconds
              if (expiresIn < 30000) {
                await handleRefresh();
              }
            }
          }
        );
      } catch (error) {
        console.warn("Failed to add app state listener:", error);
      }
    };

    addAppListener();

    return () => {
      if (currentAppListener) {
        try {
          currentAppListener.remove();
        } catch (error) {
          console.warn("Failed to remove app listener:", error);
        }
        currentAppListener = null;
      }
    };
  }, [state.isAuthenticated, state.exp, handleRefresh]);

  return (
    <SessionContext.Provider
      value={{ ...state, signIn, signOut, setUnauthorized }}
    >
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Custom hook to use the session context.
 * @returns The session context value.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

export default SessionProvider;
